import { usePathname, useRouter } from 'expo-router';
import { useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { View } from 'react-native';
import { SvgXml } from 'react-native-svg';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { Button, Text } from '@/ui';

import { darkErrorBackground, lightErrorBackground } from './backgrounds';

type ErrorStatus = 404 | 403;

const copy: Record<ErrorStatus, { title: string; message: string }> = {
  404: {
    title: 'Oops! Page is not found',
    message: 'Could not find requested resource',
  },
  403: {
    title: 'No Access',
    message: "We're sorry but you don't have access to this page",
  },
};

export const ErrorPage = ({ status }: { status: ErrorStatus }) => {
  const { styles } = useStyles(stylesheet);
  const pathname = usePathname();
  const router = useRouter();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const compact = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const dark = UnistylesRuntime.themeName === 'dark';
  const partner = pathname === '/partner' || pathname.startsWith('/partner/');

  const onLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setSize((current) =>
      current.width === width && current.height === height
        ? current
        : { width, height }
    );
  };

  return (
    <View style={styles.root}>
      <View style={styles.background} pointerEvents="none" onLayout={onLayout}>
        {size.width > 0 ? (
          <SvgXml
            xml={dark ? darkErrorBackground : lightErrorBackground}
            width={size.width}
            height={size.height}
          />
        ) : null}
        <View style={dark ? styles.overlayDark : styles.overlayLight} />
      </View>
      <View style={[styles.content, status === 403 && styles.contentTight]}>
        <View
          style={[
            styles.copy,
            compact ? styles.copyCompact : null,
            status === 403 && !compact ? styles.copyTight : null,
          ]}
        >
          <Text style={compact ? styles.codeCompact : styles.code}>
            {status}
          </Text>
          <Text style={compact ? styles.titleCompact : styles.title}>
            {copy[status].title}
          </Text>
          <Text
            type="bodyRegular"
            style={dark ? styles.messageDark : styles.message}
          >
            {copy[status].message}
          </Text>
        </View>
        <Button
          type="primary"
          text="Back to Home"
          textStyle={styles.buttonLabel}
          style={styles.button}
          onPress={() => {
            router.replace(partner ? '/partner/dashboard' : '/');
          }}
        />
      </View>
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  background: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'hidden',
  },
  overlayLight: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
  },
  overlayDark: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    gap: 24,
  },
  contentTight: {
    gap: 16,
  },
  copy: {
    width: '100%',
    alignItems: 'center',
    gap: 16,
  },
  copyCompact: {
    gap: 8,
  },
  copyTight: {
    gap: 4,
  },
  code: {
    fontSize: 160,
    fontWeight: 400,
    lineHeight: 192,
    letterSpacing: 0.4,
    textAlign: 'center',
  },
  codeCompact: {
    fontSize: 100,
    fontWeight: 400,
    lineHeight: 120,
    letterSpacing: 0.4,
    textAlign: 'center',
  },
  title: {
    fontSize: 48,
    fontWeight: 400,
    lineHeight: 57.6,
    letterSpacing: 0.4,
    textAlign: 'center',
  },
  titleCompact: {
    fontSize: 24,
    fontWeight: 500,
    lineHeight: 28.8,
    textAlign: 'center',
    textTransform: 'capitalize',
  },
  message: {
    textAlign: 'center',
    color: theme.colors.text.secondary,
  },
  messageDark: {
    textAlign: 'center',
    color: theme.colors.text.primary,
  },
  button: {
    width: 222,
  },
  buttonLabel: {
    textTransform: 'capitalize',
  },
}));
